/**
 * Third-party brand marks for the sign-in buttons.
 *
 * Every mark here is the provider's own artwork, reproduced verbatim from a
 * primary source — not redrawn, not re-minified, not re-fitted. All three
 * companies' guidelines forbid altering their mark, so treat the path data and
 * the fill values in this file as immutable: if one looks wrong, it is not.
 *
 * They are inline rather than files because a strict CSP blocks every external
 * request, and decorative (`aria-hidden`) because each button's visible label
 * already names the provider.
 */

/**
 * Google's four-colour "G", from the reference assets on developers.google.com
 * (branding_guideline_sample_lt_rd_sl.svg and two siblings — the paths were
 * byte-identical across all three).
 *
 * DO NOT "CORRECT" THE RED. It is #E94235 here, which is *not* Google's
 * canonical brand red #EA4335 that Wikimedia and every npm icon pack use.
 * Google's own shipped sign-in assets carry #E94235, and matching the asset we
 * are told to reproduce exactly beats matching a palette swatch. Yellow is
 * likewise #FBBC04, not the older #FBBC05.
 *
 * The viewBox is the original's crop expressed directly: Google wraps the mark
 * in a `clipPath` of rect(20x20) translated (10,10), which is exactly the paths'
 * bounding box, so this is lossless — and it avoids duplicate `clipPath` ids
 * when several marks are inlined on one page.
 *
 * Rules this must keep satisfying: standard colour version only, never
 * recoloured or resized relative to its own artwork, and on a light, dark or
 * neutral plate — our button is `bg-buds-surface`, which is `#ffffff`.
 */
export function GoogleMark() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="10 10 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className="size-5 shrink-0"
    >
      <path
        d="M29.6 20.2273C29.6 19.5182 29.5364 18.8364 29.4182 18.1818H20V22.05H25.3818C25.15 23.3 24.4455 24.3591 23.3864 25.0682V27.5773H26.6182C28.5091 25.8364 29.6 23.2727 29.6 20.2273Z"
        fill="#4285F4"
      />
      <path
        d="M20 30C22.7 30 24.9636 29.1045 26.6181 27.5773L23.3863 25.0682C22.4909 25.6682 21.3454 26.0227 20 26.0227C17.3954 26.0227 15.1909 24.2636 14.4045 21.9H11.0636V24.4909C12.7091 27.7591 16.0909 30 20 30Z"
        fill="#34A853"
      />
      <path
        d="M14.4045 21.9C14.2045 21.3 14.0909 20.6591 14.0909 20C14.0909 19.3409 14.2045 18.7 14.4045 18.1V15.5091H11.0636C10.3864 16.8591 10 18.3864 10 20C10 21.6136 10.3864 23.1409 11.0636 24.4909L14.4045 21.9Z"
        fill="#FBBC04"
      />
      <path
        d="M20 13.9773C21.4681 13.9773 22.7863 14.4818 23.8227 15.4727L26.6909 12.6045C24.9591 10.9909 22.6954 10 20 10C16.0909 10 12.7091 12.2409 11.0636 15.5091L14.4045 18.1C15.1909 15.7364 17.3954 13.9773 20 13.9773Z"
        fill="#E94235"
      />
    </svg>
  );
}

/**
 * The Microsoft symbol, from ms-symbollockup_mssymbol_19.svg on
 * learn.microsoft.com (verified byte-for-byte, SHA-256
 * 929f48f8…d7ecd84a). Quadrants are red top-left, green top-right, blue
 * bottom-left, yellow bottom-right — SVG's y axis points down.
 *
 * Rendered at 21px rather than 20: Microsoft builds ~1 unit of clear space
 * into all four sides of its 21-unit viewBox, so at a nominal 20px the visible
 * mark would be ~19px and read as smaller than the Google G beside it. 21px
 * makes the *visible* marks match, and lands every rect edge on a whole pixel
 * so the gutters stay crisp instead of anti-aliasing to grey.
 *
 * Microsoft's own asset ships a `<title>MS-SymbolLockup</title>`, dropped here:
 * as the first child it becomes the graphic's accessible name, so a screen
 * reader would read Microsoft's internal filename out inside a button that
 * already says "Continue with Microsoft".
 */
export function MicrosoftMark() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 21 21"
      aria-hidden="true"
      focusable="false"
      className="size-[21px] shrink-0"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * The Apple mark, from the "small" variant inside Apple's own
 * appleid.auth.js (SHA-256 d1fc966b…64ae943f).
 *
 * Two things here look wrong and are not:
 *
 * 1. **The viewBox is 12x44, not square.** This is a button-height lockup, not
 *    an icon: the glyph deliberately fills only ~33% of its height, and the
 *    rest is Apple's built-in padding. The HIG says to match the height of the
 *    logo to the height of the button, which is why this renders at `h-full`
 *    (48px, our button height) and lets width follow. Cropping the viewBox to
 *    the glyph would make it a tidy 20px square and is *explicitly forbidden*:
 *    "Never crop the Apple-provided artwork to decrease its built-in padding".
 *    The visible glyph therefore comes out ~16px against the others' 20px —
 *    that is what a compliant Sign in with Apple button looks like.
 *
 * 2. **The fill is hard-coded black, not `currentColor`.** Every other mark
 *    could inherit, but the HIG allows the Apple logo to be black or white and
 *    nothing else: "don't use custom colors". Our button text is `#003020`, a
 *    dark green — close enough to read as black, and still a custom colour.
 *    If this button ever goes on a dark plate, switch to `#fff`, not to a
 *    token.
 */
export function AppleMark() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="6 0 12 44"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className="h-full w-auto shrink-0"
    >
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <path
          fill="#000000"
          fillRule="nonzero"
          d="M12.2337427,16.9879688 C12.8896607,16.9879688 13.7118677,16.5445313 14.2014966,15.9532812 C14.6449341,15.4174609 14.968274,14.6691602 14.968274,13.9208594 C14.968274,13.8192383 14.9590357,13.7176172 14.9405591,13.6344727 C14.2107349,13.6621875 13.3330982,14.1241016 12.8065162,14.7430664 C12.3907935,15.2142188 12.012024,15.9532812 12.012024,16.7108203 C12.012024,16.8216797 12.0305005,16.9325391 12.0397388,16.9694922 C12.0859302,16.9787305 12.1598365,16.9879688 12.2337427,16.9879688 Z M9.92417241,28.1662891 C10.8202857,28.1662891 11.2175318,27.5658008 12.3353638,27.5658008 C13.4716724,27.5658008 13.721106,28.1478125 14.7188404,28.1478125 C15.6980982,28.1478125 16.3540162,27.2424609 16.972981,26.3555859 C17.6658521,25.339375 17.9522388,24.3416406 17.9707154,24.2954492 C17.9060474,24.2769727 16.0306763,23.5101953 16.0306763,21.3576758 C16.0306763,19.491543 17.5088013,18.6508594 17.5919459,18.5861914 C16.612688,17.1819727 15.1253248,17.1450195 14.7188404,17.1450195 C13.6194849,17.1450195 12.7233716,17.8101758 12.1598365,17.8101758 C11.5501099,17.8101758 10.7463794,17.1819727 9.79483648,17.1819727 C7.98413335,17.1819727 6.14571538,18.6785742 6.14571538,21.5054883 C6.14571538,23.2607617 6.8293482,25.1176563 7.67003179,26.3186328 C8.39061773,27.3348438 9.01882085,28.1662891 9.92417241,28.1662891 Z"
        />
      </g>
    </svg>
  );
}
